import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency,
   formatDate
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

type ArActivityRow = {
   rowId: string;
   accountName: string;
   accountNo: string;
   accountType: string;
   accountStatus: string;
   creditLimitLoc: string;
   name: string;
   invoiceNo: string;
   folioNo: string;
   checkedOutDate: string;
   checkedOutDateRaw: string;
   amount: string;
   amountRaw: number;
   currency: string;
   hasRealTyallsRowId: boolean;
};

function getArActivityRows(reportJson: any): any[] {
   return getReportArray(reportJson, 'ARActivity');
}

function parseAmountOrNull(value: unknown): number | null {
   return asNumberOrNull(value, {
      removeGroupingSeparators: true
   });
}

function getRowId(row: any): string {
   const tyallsRowId = getRealTyallsRowId(row);

   if (tyallsRowId) {
      return tyallsRowId;
   }

   return `ar-activity:${asString(row?.InvoiceNo)}:${asString(
      row?.FolioNo
   )}:${asString(row?.Name)}`;
}

function normaliseRows(reportJson: any): ArActivityRow[] {
   return getArActivityRows(reportJson)
      .map((row) => {
         const amountRaw = parseAmountOrNull(row?.Amount) ?? 0;
         const checkedOutDateRaw = asString(row?.CheckedOutDate);

         return {
            rowId: getRowId(row),
            accountName: asString(row?.AccountName),
            accountNo: asString(row?.AccountNo),
            accountType: asString(row?.AccountType),
            accountStatus: asString(row?.AccountStatus),
            creditLimitLoc: asString(row?.CreditLimitLoc),
            name: asString(row?.Name),
            invoiceNo: asString(row?.InvoiceNo),
            folioNo: asString(row?.FolioNo),
            checkedOutDate: formatDate(checkedOutDateRaw),
            checkedOutDateRaw,
            amount: formatCurrency(amountRaw),
            amountRaw,
            currency: asString(row?.Currency),
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter((row) => {
         return (
            row.accountName ||
            row.accountNo ||
            row.name ||
            row.invoiceNo ||
            row.folioNo
         );
      });
}

function sortRows(rows: ArActivityRow[]): ArActivityRow[] {
   return [...rows].sort((a, b) => {
      return (
         a.accountName.localeCompare(b.accountName) ||
         a.accountNo.localeCompare(b.accountNo) ||
         a.name.localeCompare(b.name) ||
         a.invoiceNo.localeCompare(b.invoiceNo)
      );
   });
}

function flattenRows(reportJson: any): ReportRowVm[] {
   return sortRows(normaliseRows(reportJson)).map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'arActivity',

         accountName: row.accountName,
         accountNo: row.accountNo,
         accountType: row.accountType,
         accountStatus: row.accountStatus,
         creditLimitLoc: row.creditLimitLoc,
         name: row.name,
         invoiceNo: row.invoiceNo,
         folioNo: row.folioNo,
         checkedOutDate: row.checkedOutDate,
         checkedOutDateRaw: row.checkedOutDateRaw,
         amount: row.amount,
         amountRaw: row.amountRaw,
         currency: row.currency
      }
   }));
}

export const AR_ACTIVITY_DEFINITION: ReportDefinition = {
   reportType: 'ARActivity',
   version: 1,
   title: 'AR Activity',

   header: {
      title: 'AR Activity',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'arActivity'
         );

         const totalAmount = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['amountRaw']);
            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length,
               tone: dataRows.length > 0 ? 'info' : 'success'
            },
            {
               label: 'Total Amount',
               value: formatCurrency(totalAmount),
               tone: totalAmount !== 0 ? 'info' : 'success'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['arActivity']
   },

   columns: [
      { key: 'accountName', header: 'Account Name', width: '230px', maxChars: 80 },
      { key: 'accountNo', header: 'Account No.', width: '120px' },
      { key: 'accountType', header: 'Account Type', width: '120px' },
      { key: 'accountStatus', header: 'Account Status', width: '130px' },
      { key: 'creditLimitLoc', header: 'Credit Limit / LOC', width: '160px' },
      { key: 'name', header: 'Name', width: '240px', maxChars: 90 },
      { key: 'invoiceNo', header: 'Invoice No.', width: '120px' },
      { key: 'folioNo', header: 'Folio No.', width: '120px' },
      { key: 'checkedOutDate', header: 'Checked Out Date', width: '150px' },
      { key: 'amount', header: 'Amount', width: '130px' }
   ],

   flattenRows,

   filter: {
      fields: [
         { key: 'accountName', label: 'Account Name', type: 'string' },
         { key: 'accountNo', label: 'Account No.', type: 'string' },
         { key: 'accountType', label: 'Account Type', type: 'string' },
         { key: 'accountStatus', label: 'Account Status', type: 'string' },
         { key: 'creditLimitLoc', label: 'Credit Limit / LOC', type: 'string' },
         { key: 'name', label: 'Name', type: 'string' },
         { key: 'invoiceNo', label: 'Invoice No.', type: 'string' },
         { key: 'folioNo', label: 'Folio No.', type: 'string' },
         {
            key: 'checkedOutDate',
            label: 'Checked Out Date',
            type: 'date',
            getValue: (row) => row.cells['checkedOutDateRaw']
         },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         }
      ]
   }
};
